import React, { useState, useEffect, useImperativeHandle, forwardRef, useMemo, useCallback, useRef } from 'react';
import {
    DataGrid,
    GridToolbar,
    GridRowModes,
    GridActionsCellItem,
    useGridApiRef,
    gridFilteredSortedRowIdsSelector,
} from '@mui/x-data-grid';
import {
    getGridStringOperatorsWithComparison,
    getGridNumericOperatorsWithNotLike,
    getGridDateOperatorsWithNotLike,
} from '../../../Utils/DataGridFilterOperators';
import { styled } from '@mui/material/styles';
import {
    IconButton,
    RadioGroup,
    FormControlLabel as MuiFormControlLabel,
    InputLabel,
    Autocomplete,
    TextField,
    FormControl,
    Select,
    MenuItem,
    Box,
    Typography,
    Chip,
    Tooltip,
    Popover,
    Checkbox,
    Button,
    CircularProgress,
    Popper,
    useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import { FitColumnsButton, useFitTableColumns } from '../../../hooks/useFitTableColumns';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import NoDocumentScanned from '../../../assests/images/svgs/NoDocumentScanned.svg';
import DocumentItemIcon from '../../../assests/images/Icons/documentItem.svg';
import FormControlLabel from '@mui/material/FormControlLabel';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enUS, arSA, he } from 'date-fns/locale'; // Import desired locales
import { useTranslation } from 'react-i18next';
import editIcon from '../../../assests/images/Icons/editIcon.svg';
import deleteIcon from '../../../assests/images/Icons/deleteIcon.svg';
import saveIcon from '../../../assests/images/Icons/saveIcon.svg';
import cancelIcon from '../../../assests/images/Icons/cancelIcon.svg';
import DocumentDraggableDialog from './DocumentDraggableDialog';
import { roundVatToInteger } from '../../../Utils/GetVatValue.js';
import { getDescriptionCellClass } from '../../../Utils/documentDescriptionFlags';
import styles from './DocumentDataGrid.module.scss';

const STORAGE_KEY_DOCUMENT_VIEW = 'documentDataGrid_view';
const STORAGE_KEY_DOCUMENT_COLUMN_WIDTHS = 'documentDataGrid_columnWidths';
const STORAGE_KEY_DOCUMENT_COLUMN_VISIBILITY = 'documentDataGrid_columnVisibility';
const STORAGE_KEY_DOCUMENT_COLUMN_VISIBILITY_CUSTOM = 'documentDataGrid_columnVisibility_custom';
const TABLE_VIEWS = ['compact', 'standard', 'full', 'custom'];
const DEFAULT_HIDDEN_COLUMNS = { accountCodeId: false };
const VIEW_INCLUDES_TIER = {
    compact: ['priority', 'compact'],
    standard: ['priority', 'compact', 'standard'],
    full: ['priority', 'compact', 'standard', 'full'],
    custom: ['priority', 'compact', 'standard', 'full'],
};

const StyledProjectsPopper = styled(Popper)({
    '& .MuiPaper-root': {
        borderRadius: '8px',
        background: '#ffffff',
        border: '1px solid #e5e7eb',
        boxShadow: '0 12px 28px rgba(20, 30, 43, 0.16)',
        overflow: 'hidden',
    },
    '& .MuiAutocomplete-listbox': {
        padding: 0,
        textAlign: 'start',
    },
    '& .MuiAutocomplete-option': {
        justifyContent: 'flex-start',
        textAlign: 'start',
        fontSize: 'var(--xtram-text-sm, 13px)',
        fontWeight: 500,
        minHeight: 'var(--xtram-menu-item-height, 36px)',
        '&[aria-selected="true"]': {
            background: 'rgba(48, 79, 255, 0.1) !important',
            color: '#304fff',
            fontWeight: 700,
        },
        '&.Mui-focused, &:hover': {
            background: '#304fff !important',
            color: '#ffffff',
        },
    },
});

const TABLE_VIEW_MENU_PROPS = {
    PaperProps: {
        sx: {
            mt: 0.5,
            borderRadius: '8px',
            border: '1px solid #e5e7f6',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            overflow: 'hidden',
            minWidth: 'var(--xtram-menu-width)',
            '& .MuiList-root': { p: 0 },
            '& .MuiMenuItem-root': {
                minHeight: 'var(--xtram-menu-item-height)',
                px: 'var(--xtram-menu-item-pad-x)',
                py: 'var(--xtram-menu-item-pad-y)',
                fontSize: 'var(--xtram-text-sm)',
                fontWeight: 400,
                lineHeight: 1.35,
                color: '#696665',
                transition: 'background-color 0.2s ease, color 0.2s ease',
                '&:not(:last-of-type)': { borderBottom: '1px solid #f0f0f0' },
                '&:hover': { backgroundColor: '#304fff', color: '#ffffff' },
                '&.Mui-selected': {
                    backgroundColor: 'rgba(48, 79, 255, 0.1)',
                    color: '#304fff',
                    fontWeight: 500,
                },
                '&.Mui-selected:hover': { backgroundColor: '#304fff', color: '#ffffff' },
            },
        },
    },
};

const COLUMN_PANEL_PAPER_SX = {
    p: 0,
    mt: 0.5,
    maxHeight: 360,
    width: 'var(--xtram-menu-width)',
    minWidth: 'var(--xtram-menu-width)',
    maxWidth: 'var(--xtram-menu-width)',
    borderRadius: '8px',
    border: '1px solid #e5e7f6',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    overflow: 'hidden',
};

// Filter operators from Utils/DataGridFilterOperators.js

// Styled DataGrid component
const DOCUMENT_FIT_WEIGHTS = {
    document: 96,
    processedBy: 110,
    createdBy: 110,
    isLocked: 84,
    processed: 92,
    accountCaseId: 112,
    reference: 84,
    reference2: 84,
    confirmationNumber: 124,
    description: 168,
    reportingMonthDate: 96,
    registrationMonthDate: 96,
    uploadDate: 100,
    documentDate: 100,
    amount: 88,
    vat: 68,
    vatAmount: 84,
    netAmount: 84,
    creditAccount: 150,
    debitAccount: 150,
    sortCodeId: 128,
    uploadNumber: 84,
};

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
    width: '100%',
    '& .MuiAutocomplete-root': {
        display: 'flex',
        justifyContent: 'center',
        alignItems: "center"

    },
    '& *': {
        boxSizing: 'border-box !important',

    },
    '& .MuiDataGrid-columnHeaders': {
        // opacity: 0.7,
        backgroundColor: '#E3E7FE ',
        minHeight: '42px !important',
        maxHeight: '42px !important',
        lineHeight: '42px',
    },
    '& .MuiDataGrid-columnHeader': {
        backgroundColor: '#E3E7FE ',
        whiteSpace: 'normal', // Allow text wrapping
        wordWrap: 'break-word', // Enable breaking long words
        textAlign: 'center', // Center align the header text
        display: 'flex',
        alignItems: 'center', // Vertically center the text
        justifyContent: 'center', // Horizontally center the text
        padding: '0 8px',
        minHeight: '42px !important',
        maxHeight: '42px !important',
        height: '42px !important',
        boxSizing: 'border-box',
    },
    '& .MuiDataGrid-columnHeaderTitle': {
        color: '#1222b3',
        fontFamily: 'Montserrat',
        fontSize: '13px',
        fontWeight: 600,
        lineHeight: 'normal',
    },
    '& .MuiDataGrid-sortIcon': {
        opacity: '0.8 !important',
        color: '#1222b3',
    },
    '& .MuiDataGrid-menuIconButton': {
        color: '#1222b3',
    },
    // Override MUI default row-reverse for right-aligned headers (RTL)
    '& .MuiDataGrid-columnHeader--alignRight .MuiDataGrid-columnHeaderDraggableContainer, & .MuiDataGrid-columnHeader--alignRight .MuiDataGrid-columnHeaderTitleContainer': {
        flexDirection: 'row',
    },
    // Override MUI default flex-end for right-aligned cells (RTL)
    '& .MuiDataGrid-cell--textRight': {
        textAlign: 'left',
        justifyContent: 'flex-start',
    },
    '& .MuiDataGrid-main, & .MuiDataGrid-overlayWrapper': {
        backgroundColor: '#ffffff',
    },
    '& .MuiDataGrid-virtualScroller': {
        backgroundColor: '#ffffff',
        overflowX: 'auto',
        scrollbarWidth: 'var(--register-scrollbar-width, var(--xtram-scrollbar-width, thin))',
        scrollbarColor: '#d8daf2 transparent',
        borderRadius: 'inherit',
    },
    '& .MuiDataGrid-virtualScroller::-webkit-scrollbar': {
        width: '6px',
        height: '6px',
    },
    '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track': {
        background: 'transparent',
    },
    '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb': {
        background: '#d8daf2',
        borderRadius: 'var(--xtram-radius-pill, 999px)',
    },
    '& .MuiDataGrid-scrollbar': {
        scrollbarWidth: 'var(--register-scrollbar-width, var(--xtram-scrollbar-width, thin))',
        scrollbarColor: '#d8daf2 transparent',
        borderRadius: 'inherit',
    },
    '& .MuiDataGrid-scrollbar::-webkit-scrollbar': {
        width: '6px',
        height: '6px',
    },
    '& .MuiDataGrid-scrollbar::-webkit-scrollbar-track': {
        background: 'transparent',
    },
    '& .MuiDataGrid-scrollbar::-webkit-scrollbar-thumb': {
        background: '#d8daf2',
        borderRadius: 'var(--xtram-radius-pill, 999px)',
    },
    '& .MuiDataGrid-scrollbar.MuiDataGrid-scrollbar--horizontal': {
        display: 'block',
        height: '6px',
    },
    '& .MuiDataGrid-cell': {
        borderTop: '1px solid rgba(191, 191, 193, 0.56)',
        display: 'flex',
        alignItems: 'center',
        whiteSpace: 'normal',
        wordWrap: 'break-word',
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
        lineHeight: 1.3,
        overflow: 'hidden',
        /* ~4 wrapped lines + padding — don't grow much beyond that */
        minHeight: '50px',
        maxHeight: '90px',
    },
    '& .MuiDataGrid-row': {
        minHeight: '50px',
        maxHeight: '90px !important',
    },
    '& .MuiDataGrid-cellContent, & .MuiDataGrid-cell > span, & .MuiDataGrid-cell .description-cell': {
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 4,
        overflow: 'hidden',
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
        maxWidth: '100%',
        lineHeight: 1.3,
    },
    '& .MuiDataGrid-row:hover': {
        backgroundColor: 'rgba(227, 230, 255, 0.3)',
    },
    '& .MuiDataGrid-cell:focus': {
        outline: 'none',
    },
    '& .disableUser': {
        pointerEvents: 'none',
        opacity: 0.5,
    },
    '&.fit-to-screen .MuiDataGrid-columnHeader': {
        whiteSpace: 'nowrap',
        padding: '0 4px',
    },
    '&.fit-to-screen .MuiDataGrid-columnHeaderTitleContainer': {
        overflow: 'hidden',
    },
    '&.fit-to-screen .MuiDataGrid-columnHeaderTitle': {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    '&.fit-to-screen .MuiDataGrid-cell': {
        whiteSpace: 'nowrap',
        wordBreak: 'normal',
        overflowWrap: 'normal',
        lineHeight: 1.2,
        minHeight: '44px',
        maxHeight: '48px',
        padding: '0 4px',
    },
    '&.fit-to-screen .MuiDataGrid-row': {
        minHeight: '44px',
        maxHeight: '48px !important',
    },
    '&.fit-to-screen .MuiDataGrid-cellContent, &.fit-to-screen .MuiDataGrid-cell > span, &.fit-to-screen .MuiDataGrid-cell .description-cell, &.fit-to-screen .MuiDataGrid-cell p': {
        display: 'block',
        WebkitLineClamp: 'unset',
        whiteSpace: 'nowrap',
        wordBreak: 'normal',
        overflowWrap: 'normal',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        maxWidth: '100%',
        margin: 0,
    },
    '&.fit-to-screen .processed, &.fit-to-screen .unprocessed, &.fit-to-screen .status-permanent, &.fit-to-screen .status-temporary': {
        height: '22px',
        padding: '0 6px',
        fontSize: '11px',
        whiteSpace: 'nowrap',
        maxWidth: '100%',
        overflow: 'hidden',
    },
    '&.fit-to-screen .iframe-container': {
        width: '100%',
        maxWidth: '100%',
    },
    '&.fit-to-screen .iframe-container iframe, &.fit-to-screen .iframe-container img': {
        width: '100% !important',
        maxWidth: '100%',
        height: '32px !important',
        objectFit: 'contain',
    },
    // New styles for edit mode
    '& .MuiDataGrid-cell--editing': {
        paddingTop: '8px',
        paddingBottom: '8px',
        paddingLeft: '8px',
        paddingRight: '8px',
    },
    '& .MuiDataGrid-row--editing .MuiDataGrid-cell': {
        borderRight: 'none',
    },
    '& .MuiDataGrid-row--editing .MuiDataGrid-cell:not(:last-child)': {
        borderRight: '8px solid transparent',
    },
    '& .MuiDataGrid-cell--editing': {
        padding: 0,
        backgroundColor: 'inherit',
    },
    '& .row-locked': {
        backgroundColor: '#f3f3f3 !important', // Light gray background
        pointerEvents: 'none', // Disable interaction
        opacity: 0.6, // Dim the row
        position: 'relative',
        '&::after': {
            content: '"🔒"',
            position: 'absolute',
            right: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            fontSize: '16px',
            color: '#d32f2f', // Red lock
        },
    },
    // 🔹 duplicated description turns bold red
    // 🔴 first style you already had
    '& .duplicate-desc': {
        color: '#d32f2f',     // red
        fontWeight: 600,
    },

    // 🟠 second style for “שוכפל מ …”
    '& .duplicate-from-desc': {
        color: '#ff9800',     // orange (pick any)
        fontWeight: 600,
    },
}));


const fullCellTextFieldStyle = {
    width: '100%',
    height: '100%',
    '& .MuiInputBase-root': {
        height: '100%',
        padding: 0,
        backgroundColor: 'transparent',
    },
    '& .MuiInputBase-input': {
        height: '100%',
        padding: '0 8px',
        boxSizing: 'border-box',
        backgroundColor: 'transparent',
    },
};


const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = ('0' + date.getDate()).slice(-2);
    const month = ('0' + (date.getMonth() + 1)).slice(-2);
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
};

const DocumentDataGrid = forwardRef((props, ref) => {
    const {
        t, // Translation function
        type, // 'Incomes' or 'Expenses'
        client, // Client data
        data, // Array of documents to display
        isRange, // Boolean indicating if date range is selected
        subAccountantOptions,
        isClosedReportingMonth, // Boolean indicating if the reporting month is closed
        accountCardOptions, // Options for account cards
        creditAccountOptions, // Options for credit accounts
        debitAccountOptions, // Options for debit accounts
        sortCodeIdOptions, // Options for sort codes
        openPdfInNewTab, // Function to open PDF in a new tab
        formatDateNew, // Function to format dates
        formatMonthYearDate, // Function to format month and year dates
        beautifyNumber, // Function to format numbers
        Constants, // Constants used in the application
        handleDeleteClick, // Function to handle deleting a row
        handleSave,
        handleCancel,
        direction,
        setEditedItem,
        editedItem,
        setEditedPopupItem,
        handleEdit,
        setCurrentRowData,
        setOpenInlineEditModal,
        handleSelectRows,
        handleRightClickAction,
        complexDepositsChecked,
        setDebitCreditSum,
        setCreditSum,
        setDebitSum,
        userTypeContext,
        permissionsState,
        allowAllSelect,
        hideStatus = false,
        byProjectsFilterActive = false,
        onByProjectsFilterToggle = () => {},
        loadingSegmentations = false,
        projectAutocompleteOptions = [],
        selectedFromSegmentation = null,
        selectedToSegmentation = null,
        onFromSegmentationChange = () => {},
        onToSegmentationChange = () => {},
        onFilteredRowsChange,
        toolbarExtra = null,
        getRowClassName,
    } = props;

    const { i18n } = useTranslation();
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg')); // < 1200px
    // Real tablets/phones: Safari/iOS won't paint PDF inside tiny iframes (Chrome device mode still can).
    const isTouchPrimary = useMediaQuery('(hover: none), (pointer: coarse)');
    const preferDocIconThumb =
        isTouchPrimary ||
        (typeof navigator !== 'undefined' &&
            (/iPad|iPhone|iPod/.test(navigator.userAgent) ||
                (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)));
    const apiRef = useGridApiRef();
    const onFilteredRowsChangeRef = useRef(onFilteredRowsChange);
    onFilteredRowsChangeRef.current = onFilteredRowsChange;
    const [tableView, setTableView] = useState(() => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY_DOCUMENT_VIEW);
            return TABLE_VIEWS.includes(saved) ? saved : 'standard';
        } catch {
            return 'standard';
        }
    });
    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY_DOCUMENT_VIEW, tableView);
        } catch (_) {}
    }, [tableView]);
    const [columnWidths, setColumnWidths] = useState(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY_DOCUMENT_COLUMN_WIDTHS);
            if (!raw) return {};
            const parsed = JSON.parse(raw);
            return typeof parsed === 'object' && parsed !== null ? parsed : {};
        } catch {
            return {};
        }
    });
    const [columnVisibilityModel, setColumnVisibilityModel] = useState(() => {
        try {
            const savedView = localStorage.getItem(STORAGE_KEY_DOCUMENT_VIEW);
            if (savedView === 'custom') {
                const rawCustom = localStorage.getItem(STORAGE_KEY_DOCUMENT_COLUMN_VISIBILITY_CUSTOM);
                if (rawCustom) {
                    const parsed = JSON.parse(rawCustom);
                    if (typeof parsed === 'object' && parsed !== null) return { ...DEFAULT_HIDDEN_COLUMNS, ...parsed };
                }
            }
            const raw = localStorage.getItem(STORAGE_KEY_DOCUMENT_COLUMN_VISIBILITY);
            if (!raw) return { ...DEFAULT_HIDDEN_COLUMNS };
            const parsed = JSON.parse(raw);
            const saved = typeof parsed === 'object' && parsed !== null ? parsed : {};
            return { ...DEFAULT_HIDDEN_COLUMNS, ...saved };
        } catch {
            return { ...DEFAULT_HIDDEN_COLUMNS };
        }
    });
    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY_DOCUMENT_COLUMN_VISIBILITY, JSON.stringify(columnVisibilityModel));
            if (tableView === 'custom') {
                localStorage.setItem(STORAGE_KEY_DOCUMENT_COLUMN_VISIBILITY_CUSTOM, JSON.stringify(columnVisibilityModel));
            }
        } catch (_) {}
    }, [columnVisibilityModel, tableView]);
    const handleColumnVisibilityModelChange = (newModel) => {
        setColumnVisibilityModel({ ...DEFAULT_HIDDEN_COLUMNS, ...newModel });
    };
    const [columnsPanelAnchor, setColumnsPanelAnchor] = useState(null);
    const [selectedRows, setSelectedRows] = useState([]);
    const [originalItem, setOriginalItem] = useState(null);
    const [rows, setRows] = useState(data); // Initialize with data prop
    const [rowModesModel, setRowModesModel] = useState({});
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedTotals, setSelectedTotals] = useState({
        amount: 0,
        vatAmount: 0,
        netAmount: 0
    });

    // Define the deselectRow function to remove an id from selectedRows
    const deselectRows = (ids) => {


        setSelectedRows((prevSelectedRows) => prevSelectedRows.filter(rowId => !ids.includes(rowId)));
    };

    const getAlignment = () => {
        return i18n.language === 'en' ? 'left' : 'left';
    };

    // Memoize filter operators so column defs are stable (avoids "once show once no" when using valueGetter)
    const stringFilterOperators = useMemo(() => getGridStringOperatorsWithComparison(t), [t]);
    const numericFilterOperators = useMemo(() => getGridNumericOperatorsWithNotLike(t), [t]);
    const dateFilterOperators = useMemo(() => getGridDateOperatorsWithNotLike(t), [t]);



    // Expose the deselectRow function to the parent via ref
    useImperativeHandle(ref, () => ({
        deselectRows
    }));

    const getLocale = () => {
        switch (i18n.language) {
            case 'ar':
                return arSA; // Arabic locale
            case 'he':
                return he; // Hebrew locale
            default:
                return enUS; // Default to English locale
        }
    };

    // Function to handle double-click to enter edit mode and set editedItem
    const handleCellDoubleClick = (params) => {
        const row = params?.row || {};
        const rowIndex = data.findIndex((r) => r.id === row.id); // Find the index in the original data array
        handleEdit(row, rowIndex)
    };

    const handleRowModesModelChange = (newRowModesModel) => {

        setRowModesModel(newRowModesModel);
    };

    // Function to save changes and exit edit mode
    const handleSaveClick = (id) => () => {


        setRowModesModel((prevModel) => ({
            ...prevModel,
            [id]: { mode: GridRowModes.View },
        }));
    };

    // Function to cancel edit mode
    const handleCancelClick = (id) => () => {
        setRowModesModel((prevModel) => ({
            ...prevModel,
            [id]: { mode: GridRowModes.View, ignoreModifications: true },
        }));
        if (originalItem) {
            setRows((prevRows) =>
                prevRows.map((row) => (row.id === id ? originalItem : row))
            );
        }
        setEditedItem(null);
        setOriginalItem(null);
        handleCancel(); // Call the cancel handler if needed
    };

    const processRowUpdate = (newRow, oldRow) => {
        // Handle accountCodeId changes



        if (newRow.accountCodeId !== oldRow.accountCodeId) {
            const accountCard = accountCardOptions.find(
                (option) => option.cardId === newRow.accountCodeId
            );


            newRow.accountCodeName = accountCard ? accountCard.name : '';
            newRow.accountCaseId = accountCard ? accountCard.accountCaseId : '';

            if (type === 'Incomes') {
                newRow.debitAccount = newRow.accountCodeId;
            } else {
                newRow.creditAccount = newRow.accountCodeId;
            }
        }

        // Handle sortCodeId changes
        if (newRow.sortCodeId !== oldRow.sortCodeId) {


            const selectedOption = sortCodeIdOptions.find(
                (option) => option.sortCodeId === newRow.sortCodeId
            );

            if (selectedOption) {
                const nameWithoutParentheses = selectedOption.sortCodeName
                    .replace(/\s*\(.*?\)\s*/, '')
                    .trim();

                const match = selectedOption.sortCodeName.match(/\((\d+)\)/);
                let customVat = match ? parseInt(match[1], 10) : null;

                const sortCode = selectedOption;

                if (Constants.SortCodesWith66Vat.includes(nameWithoutParentheses)) {
                    newRow.vat = Constants.Vat66;
                } else if (
                    Constants.SortCodesWithZeroVat.includes(nameWithoutParentheses) ||
                    sortCode.sortCodeVatType === 0
                ) {
                    newRow.vat = 0;
                } else if (Constants.SortCodesWith16Vat.includes(nameWithoutParentheses)) {
                    newRow.vat = 16;
                } else if (customVat !== null) {
                    newRow.vat = customVat === Constants.Vat66 ? Constants.Vat66 : customVat;
                } else {
                    newRow.vat = 100;
                }



                newRow.creditAccount = sortCode.creditAccount || newRow.creditAccount;
                newRow.debitAccount = sortCode.debitAccount || newRow.debitAccount;
            }
        }


        // Handle creditAccount changes
        if (newRow.creditAccount !== oldRow.creditAccount) {
            const accountCard = accountCardOptions.find(
                (option) => option.cardId === newRow.creditAccount
            );

            newRow.accountCodeName = accountCard ? accountCard.name : '';
            newRow.accountCodeId = accountCard ? accountCard.cardId : '';
            newRow.accountCaseId = accountCard ? accountCard.accountCaseId : '';
        }

        // Handle debitAccount changes
        if (newRow.debitAccount !== oldRow.debitAccount) {


            const accountCard = accountCardOptions.find(
                (option) => option.cardId === newRow.debitAccount
            );

            newRow.accountCodeName = accountCard ? accountCard.name : '';
            newRow.accountCodeId = accountCard ? accountCard.cardId : '';
            newRow.accountCaseId = accountCard ? accountCard.accountCaseId : '';
        }

        // Update the rows state
        setRows((prevRows) =>
            prevRows.map((row) => (row.id === newRow.id ? newRow : row))
        );



        // handleSave(newRow); // Call your save handler if needed

        return newRow;
    };



    // Helper function to parse amount values (handles both numbers and formatted strings with parentheses)
    const parseAmountValue = (value) => {
        if (value === null || value === undefined) return 0;
        
        // If it's already a number, return it
        if (typeof value === 'number') return value;
        
        // If it's a string, parse it (handling parentheses notation)
        if (typeof value === 'string') {
            const strValue = value.trim();
            // Check if it's wrapped in parentheses (negative number format)
            if (strValue.startsWith('(') && strValue.endsWith(')')) {
                const cleanValue = strValue.slice(1, -1).replace(/,/g, '');
                return -parseFloat(cleanValue) || 0;
            }
            // Remove commas and parse normally
            const cleanValue = strValue.replace(/,/g, '');
            return parseFloat(cleanValue) || 0;
        }
        
        return parseFloat(value) || 0;
    };

    // Handle selection model change
    const handleSelectionModelChange = (newSelection) => {
        setSelectedRows(newSelection);
        handleSelectRows(newSelection);

        // Calculate totals for selected rows
        if (newSelection.length > 0) {
            const selectedRowsData = data.filter((row) =>
                newSelection.includes(row.id)
            );

            let totalAmount = 0;
            let totalVatAmount = 0;
            let totalNetAmount = 0;

            selectedRowsData.forEach((row) => {
                const amount = parseAmountValue(row.amount);
                const vatAmount = parseAmountValue(row.vatAmount);
                const netAmount = parseAmountValue(row.netAmount);

                totalAmount += amount;
                totalVatAmount += vatAmount;
                totalNetAmount += netAmount;
            });

            setSelectedTotals({
                amount: Math.round(totalAmount * 100) / 100,
                vatAmount: roundVatToInteger(totalVatAmount),
                netAmount: roundVatToInteger(totalNetAmount)
            });
        } else {
            setSelectedTotals({
                amount: 0,
                vatAmount: 0,
                netAmount: 0
            });
        }

        if (complexDepositsChecked) {
            const selectedRowsData = data.filter((row) =>
                newSelection.includes(row.id)
            );

            let debitTotal = 0;
            let creditTotal = 0;
            selectedRowsData.forEach((row) => {
                const amount = parseAmountValue(row.amount);

                if (row.debitAccount) {
                    debitTotal += amount;
                } else if (row.creditAccount) {
                    creditTotal += amount;
                }
            });

            const roundedDebit = Math.round(debitTotal * 100) / 100;
            const roundedCredit = Math.round(creditTotal * 100) / 100;
            const sum = roundedDebit - roundedCredit;
            const finalSum = Math.abs(sum) < 0.1 ? 0 : sum;

            setDebitCreditSum(finalSum);
            if (setCreditSum) setCreditSum(roundedCredit);
            if (setDebitSum) setDebitSum(roundedDebit);
        } else {
            setDebitCreditSum(0);
            if (setCreditSum) setCreditSum(0);
            if (setDebitSum) setDebitSum(0);
        }

    };

    // Determine if a row is selectable
    const isRowSelectable = (params) => {
        if (userTypeContext?.toString() === Constants.UserType.Client && !permissionsState.Premium) {
            return false;
        }
        const { row } = params;

        // /* 1.  Never allow a row that is already locked                      */
        // if (row.isLocked) return false;

        /* 2.  Master switch – if ON, everything above is enough             */
        if (allowAllSelect) return true;

        if (complexDepositsChecked) {
            // Only allow selection of rows where debit or credit is present, but not both
            const hasDebit = !!row.debitAccount;
            const hasCredit = !!row.creditAccount;
            return (hasDebit && !hasCredit) || (!hasDebit && hasCredit);
        } else {
            return client?.accountManagement === Constants.AccountManagementOptions[0].name
                ? row.sortCodeId && row.creditAccount && row.debitAccount
                : row.sortCodeId;
        }
    };

    useEffect(() => {

        if (data) {
            setRows(data);
        }
    }, [data]);

    const emitFilteredRows = useCallback(() => {
        const cb = onFilteredRowsChangeRef.current;
        if (!cb) return;
        const sourceRows = (rows || []).filter((r) => r.id !== 'NEW_ROW');
        try {
            const ids = gridFilteredSortedRowIdsSelector(apiRef);
            if (!Array.isArray(ids)) {
                cb(sourceRows);
                return;
            }
            // Build a lookup so we can return rows in the grid's filtered + sorted order,
            // ensuring EditModal next/previous follows the on-screen order.
            const rowById = new Map();
            sourceRows.forEach((r) => {
                rowById.set(r.id, r);
                rowById.set(String(r.id), r);
            });
            const orderedRows = ids
                .map((id) => rowById.get(id) ?? rowById.get(String(id)))
                .filter((r) => r && r.id !== 'NEW_ROW');
            cb(orderedRows);
        } catch {
            cb(sourceRows);
        }
    }, [rows, apiRef]);

    useEffect(() => {
        const frame = requestAnimationFrame(emitFilteredRows);
        return () => cancelAnimationFrame(frame);
    }, [emitFilteredRows, rows, tableView]);


    const formattedAccountCardOptions = accountCardOptions.map((option) => ({
        value: option.cardId,
        label: option.accountCaseId
            ? `${option.name} - ${option.accountCaseId}`
            : `${option.name}`,
        name: option.name,
        accountCaseId: option.accountCaseId || '',
        cardId: option.cardId,
    }));

    // Define columns with explicit widths
    const baseColumns = [
        // Document Column — overlay so tablet taps open DraggableModal (iframe can't receive clicks)
        {
            field: 'document',
            headerName: t('Document'),
            headerAlign: getAlignment(),
            align: getAlignment(),
            flex: 1,
            viewTier: 'priority',
            renderCell: (params) => {
                const hasDoc = Boolean(params.row.documentUrl);
                const openPreview = (e) => {
                    e?.preventDefault?.();
                    e?.stopPropagation?.();
                    if (!hasDoc) return;
                    openPdfInNewTab(params.row.documentUrl);
                };

                return (
                    <div
                        className={`iframe-container${hasDoc ? '' : ` ${styles.docThumbEmpty}`}`}
                        onClick={openPreview}
                        onPointerDown={(e) => e.stopPropagation()}
                        style={{ cursor: hasDoc ? 'pointer' : 'default', position: 'relative' }}
                        title={params.row.title || t('Document')}
                    >
                        {hasDoc ? (
                            preferDocIconThumb ? (
                                <img
                                    className={styles.docThumbPresent}
                                    src={DocumentItemIcon}
                                    alt={params.row.title || t('Document')}
                                    width="100"
                                    height="50"
                                    draggable={false}
                                />
                            ) : (
                                <>
                                    <div className="iframe-overlay" aria-hidden />
                                    <iframe
                                        className="zoom-iframe"
                                        src={`${params.row.documentUrl}#page=1`}
                                        width="100px"
                                        height="50px"
                                        title={params.row.title || 'No Title'}
                                        style={{ pointerEvents: 'none', border: 'none' }}
                                        tabIndex={-1}
                                    />
                                </>
                            )
                        ) : (
                            <img
                                className={styles.docThumbPlaceholder}
                                src={NoDocumentScanned}
                                alt={t('Document') || 'No document'}
                                width="100"
                                height="50"
                                draggable={false}
                            />
                        )}
                    </div>
                );
            },
        },
        {
            field: 'processedBy',
            headerName: t('DocumentProcessedBy'),
            headerAlign: getAlignment(),
            align: getAlignment(),
            flex: 1,
            viewTier: 'standard',
            renderCell: (params) => {
                if (params.value === "-100") {
                    return t('ProcessedBySystem');
                }
                const accountant = subAccountantOptions.find(option => option.value === params.value);
                return accountant ? accountant.label : '-';
            }
        },
        {
            field: 'createdBy',
            headerName: t('DocumentCreatedBy'),
            headerAlign: getAlignment(),
            align: getAlignment(),
            flex: 1,
            viewTier: 'standard',
            sortable: true,
            valueGetter: (value, row) => {
                const uid = row?.createdByUserId;
                const utype = row?.createdByUserType;
                if (!uid && !utype) return '';
                const found = subAccountantOptions?.find(option => option.value === uid);
                if (found?.label) return found.label;
                if (utype === 'Client') return t('Client');
                return uid && utype ? `${uid} (${utype})` : (uid || utype);
            },
            renderCell: (params) => {
                const uid = params.row.createdByUserId;
                const utype = params.row.createdByUserType;
                if (!uid && !utype) return '—';
                const found = subAccountantOptions?.find(option => option.value === uid);
                if (found?.label) return found.label;
                if (utype === 'Client') return t('Client');
                return uid && utype ? `${uid} (${utype})` : (uid || utype);
            },
        },
        {
            field: 'isLocked',
            headerName: t('Status'),
            headerAlign: getAlignment(),
            align: getAlignment(),
            flex: 1,
            viewTier: 'compact',
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                const isLocked = params.row.isLocked;
                return (
                    <div className={isLocked ? 'status-permanent' : 'status-temporary'}>
                        {isLocked ? t('Permanent') : t('Temporary')}
                    </div>
                );
            }
        },
        // Document Processed Column
        {
            field: 'processed',
            headerName: t('DocumentProcessed'),
            headerAlign: getAlignment(),
            align: getAlignment(),
            flex: 1,
            viewTier: 'compact',
            type: 'singleSelect',
            valueOptions: [
                { value: 'processed', label: t('processed') },
                { value: 'unprocessed', label: t('unprocessed') },
            ],
            valueGetter: (value, row) => {
                const unprocessedIds = type === 'Incomes'
                    ? (client?.documentIds?.incomes ?? [])
                    : (client?.documentIds?.expenses ?? []);
                return unprocessedIds.includes(row?.id) ? 'unprocessed' : 'processed';
            },
            renderCell: (params) => {
                const isIdInChecks =
                    type === 'Incomes'
                        ? client?.documentIds?.incomes?.includes(params.row.id)
                        : client?.documentIds?.expenses?.includes(params.row.id);

                const handleRightClick = (event) => {
                    event.preventDefault(); // Prevent the default context menu
                    const status = isIdInChecks ? 'done' : 'notDone'; // Determine the opposite status
                    const id = params.row.id; // Get the ID of the row

                    // Call your function with Id and status
                    handleRightClickAction(id, status, params.row);
                };

                return (
                    <p
                        onContextMenu={handleRightClick} // Attach the right-click handler
                        className={isIdInChecks ? 'unprocessed' : 'processed'}
                        style={{
                            cursor: 'context-menu', // Indicate a context menu is available
                        }}
                    >
                        {isIdInChecks ? t('unprocessed') : t('processed')}
                    </p>
                );
            },
            sortComparator: (v1, v2) => {
                if (v1 === v2) return 0;
                return v1 === 'unprocessed' ? 1 : -1;
            },
        },
        {
            field: 'accountCodeId',
            headerName: 'Account Code ID',
            hide: true,
            editable: true,
            hideable: false,
            viewTier: 'full',
        },
        // Account Code Name Column
        // {
        //     field: 'accountCodeName',
        //     headerName: type === 'Incomes' ? t('Client Name') : t('Provider Name'),
        //     headerAlign: getAlignment(),
        //     align: getAlignment(),
        //     editable: false,
        //     width: 150,

        // },
        // CompanyId Column
        {
            field: 'accountCaseId',
            headerName: t('CompanyId'),
            headerAlign: getAlignment(),
            align: getAlignment(),
            editable: false,
            flex: 1,
            viewTier: 'standard',
            filterOperators: stringFilterOperators,
        },
        {
            field: 'reference2',
            headerName: t('Reference2'),
            headerAlign: getAlignment(),
            align: getAlignment(),
            editable: false,
            flex: 1,
            viewTier: 'full',
            filterOperators: stringFilterOperators,
            sortComparator: (v1, v2) => {
                const num1 = parseFloat(v1) || 0; // Convert to number, fallback to 0 if NaN
                const num2 = parseFloat(v2) || 0;
                return num1 - num2;
            },
        },
        // Reference Column
        {
            field: 'reference',
            headerName: t('Reference'),
            headerAlign: getAlignment(),
            align: getAlignment(),
            editable: false,
            flex: 1,
            viewTier: 'compact',
            filterOperators: stringFilterOperators,
            sortComparator: (v1, v2) => {
                const num1 = parseFloat(v1) || 0; // Convert to number, fallback to 0 if NaN
                const num2 = parseFloat(v2) || 0;
                return num1 - num2;
            },
        },

        // ConfirmationNumber Column
        {
            field: 'confirmationNumber',
            headerName: t('ConfirmationNumber'),
            headerAlign: getAlignment(),
            align: getAlignment(),
            editable: false,
            flex: 1,
            viewTier: 'standard',
            filterOperators: stringFilterOperators,
        },
        // Description Column
        {
            field: 'description',
            headerName: t('Description'),
            headerAlign: getAlignment(),
            align: getAlignment(),
            editable: false,
            flex: 1,
            viewTier: 'priority',
            filterOperators: stringFilterOperators,
            renderCell: (params) => {
                const descriptionText = params.value || '';

                // Highlights a wrong document type (income filed in expenses and vice versa)
                // and the duplicate markers.
                const cellClass = getDescriptionCellClass(descriptionText);

                return (
                    <div
                        className={`description-cell ${cellClass}`}
                        title={descriptionText}          // Tooltip with full text
                    >
                        {descriptionText}
                    </div>
                );
            },
        },
        // MonthReportDate Column (conditional on isRange)
        {
            field: 'reportingMonthDate',
            headerName: t('MonthReportDate'),
            headerAlign: getAlignment(),
            align: getAlignment(),
            editable: false,
            flex: 1,
            viewTier: 'full',
            filterOperators: dateFilterOperators,
            sortComparator: (v1, v2) => {
                const date1 = new Date(v1);
                const date2 = new Date(v2);
                return date1 - date2;
            },
            valueGetter: (value) => {
                if (!value) return null;
                const d = new Date(value);
                return isNaN(d.getTime()) ? null : d;
            },
            valueFormatter: (params) => formatMonthYearDate(params),
        },
        {
            field: 'registrationMonthDate',
            headerName: t('MonthRegisterDate'),
            headerAlign: getAlignment(),
            align: getAlignment(),
            editable: false,
            flex: 1,
            viewTier: 'full',
            filterOperators: dateFilterOperators,
            sortComparator: (v1, v2) => {
                const date1 = new Date(v1);
                const date2 = new Date(v2);
                return date1 - date2;
            },
            valueGetter: (value) => {
                if (!value) return null;
                const d = new Date(value);
                return isNaN(d.getTime()) ? null : d;
            },
            valueFormatter: (params) => formatMonthYearDate(params),
        },
        // Upload Date Column
        {
            field: 'uploadDate',
            headerName: t('Upload Date'),
            headerAlign: getAlignment(),
            align: getAlignment(),
            type: 'date',
            editable: false,
            flex: 1,
            viewTier: 'standard',
            filterOperators: dateFilterOperators,
            sortComparator: (v1, v2) => {
                const date1 = new Date(v1);
                const date2 = new Date(v2);
                return date1 - date2;
            },

            valueFormatter: (params) => formatDate(params),
        },
        // Document Date Column
        {
            field: 'documentDate',
            headerName: t('Document Date'),
            headerAlign: getAlignment(),
            align: getAlignment(),
            type: 'date',
            editable: false,
            flex: 1,
            viewTier: 'compact',
            filterOperators: dateFilterOperators,
            sortComparator: (v1, v2) => {
                const date1 = new Date(v1);
                const date2 = new Date(v2);
                return date1 - date2;
            },

            valueFormatter: (params) => formatDate(params),
        },
        // Amount Column
        {
            field: 'amount',
            headerName: t('Amount'),
            headerAlign: getAlignment(),
            align: getAlignment(),
            type: 'number',
            editable: false,
            flex: 1,
            viewTier: 'priority',
            filterOperators: numericFilterOperators,
            renderCell: (params) => (
                <p
                    style={{
                        color: 'green',
                        fontSize: '16px',
                        fontWeight: 'bold',
                    }}
                >
                    {params.value !== null ? beautifyNumber(params.value) : '0.00'}
                </p>
            ),

        },
        // VATPercentage Column
        {
            field: 'vat',
            headerName: t('VATPercentage'),
            headerAlign: getAlignment(),
            align: getAlignment(),
            editable: false,
            flex: 1,
            viewTier: 'full',
        },
        // VATAmount Column
        {
            field: 'vatAmount',
            headerName: t('VATAmount'),
            headerAlign: getAlignment(),
            align: getAlignment(),
            type: 'number',
            flex: 1,
            viewTier: 'standard',
            filterOperators: numericFilterOperators,
            valueFormatter: (params) => beautifyNumber(params || 0),
        },
        // NetAmount Column
        {
            field: 'netAmount',
            headerName: t('NetAmount'),
            headerAlign: getAlignment(),
            align: getAlignment(),
            type: 'number',
            flex: 1,
            viewTier: 'compact',
            filterOperators: numericFilterOperators,
            valueFormatter: (params) => beautifyNumber(params || 0),
        },
        // Credit Account Column
        {
            field: 'creditAccount',
            headerName: t('Credit Account'),
            headerAlign: getAlignment(),
            align: getAlignment(),
            editable: true,
            flex: 1,
            viewTier: 'compact',
            filterOperators: stringFilterOperators,
            valueGetter: (value, row) => {
                const option = creditAccountOptions?.find((opt) => opt.cardId === value);
                return option ? `${option.cardId} - ${option.name}` : (value != null ? String(value) : '');
            },
            renderCell: (params) => {
                const rawValue = params.row.creditAccount;
                const option = creditAccountOptions.find((opt) => opt.cardId === rawValue);
                const text = option
                    ? `${option.cardId} - ${option.name}`
                    : (rawValue != null && rawValue !== '' ? String(rawValue) : '');
                return <span title={text}>{text}</span>;
            },
        },
        // Debit Account Column
        {
            field: 'debitAccount',
            headerName: t('Debit Account'),
            headerAlign: getAlignment(),
            align: getAlignment(),
            editable: true,
            flex: 1,
            viewTier: 'compact',
            filterOperators: stringFilterOperators,
            valueGetter: (value, row) => {
                const option = debitAccountOptions?.find((opt) => opt.cardId === value);
                return option ? `${option.cardId} - ${option.name}` : (value != null ? String(value) : '');
            },
            renderCell: (params) => {
                const rawValue = params.row.debitAccount;
                const option = debitAccountOptions.find((opt) => opt.cardId === rawValue);
                const text = option
                    ? `${option.cardId} - ${option.name}`
                    : (rawValue != null && rawValue !== '' ? String(rawValue) : '');
                return <span title={text}>{text}</span>;
            },
        },
        // Code ID Column
        {
            field: 'sortCodeId',
            headerName: t('Code ID'),
            editable: true,
            headerAlign: getAlignment(),
            align: getAlignment(),
            flex: 1,
            viewTier: 'compact',
            filterOperators: stringFilterOperators,
            valueGetter: (value, row) => {
                const option = sortCodeIdOptions?.find((opt) => opt.sortCodeId === value);
                return option ? `${option.sortCodeId} - ${option.sortCodeName}` : (value != null ? String(value) : '');
            },
            renderCell: (params) => {
                const rawValue = params.row.sortCodeId;
                const option = sortCodeIdOptions.find((opt) => opt.sortCodeId === rawValue || String(opt.sortCodeId) === String(rawValue));
                const name = option ? option.sortCodeName : '';
                const code = rawValue != null && rawValue !== '' ? String(rawValue) : '';
                return (
                    <span>
                        {code} {name && `- ${name}`}
                    </span>
                );
            },
        },

        // UploadNumber Column
        {
            field: 'uploadNumber',
            headerName: t('UploadNumber'),
            headerAlign: getAlignment(),
            align: getAlignment(),
            flex: 1,
            viewTier: 'full',
            filterOperators: stringFilterOperators,
            renderCell: (params) => (
                <span>{params.value !== undefined ? params.value : 'N/A'}</span>
            ),
        },
        // Actions Column
        // {
        //     field: 'actions',
        //     type: 'actions',
        //     headerName: t('Actions'),
        //     headerAlign: getAlignment(),
        //     align: getAlignment(),
        //     width: 180,
        //     getActions: (params) => {
        //         const row = params?.row || {};
        //         const rowIndex = data.findIndex((r) => r.id === row.id); // Find the index in the original data array

        //         return [
        //             <GridActionsCellItem
        //                 icon={<img src={editIcon} alt="Edit" />}
        //                 label="Edit"
        //                 onClick={() => handleEdit(row, rowIndex)} // Pass both row and rowIndex
        //                 showInMenu={false} // Optional
        //             />,
        //             (isRange ? !row.isClosedReportingMonth : !isClosedReportingMonth) && (
        //                 <GridActionsCellItem
        //                     icon={<img src={deleteIcon} alt="Delete" />}
        //                     label="Delete"
        //                     onClick={() => handleDeleteClick(row.id)}
        //                     showInMenu={false} // Optional
        //                 />
        //             ),
        //         ].filter(Boolean); // Filter out falsey values in case the condition is false
        //     },

        // },
    ].reverse();

    const visibleColumns = useMemo(() => {
        const included = VIEW_INCLUDES_TIER[tableView] || VIEW_INCLUDES_TIER.standard;
        let cols = baseColumns
            .filter((col) => included.includes(col.viewTier))
            .map(({ viewTier, ...col }) => ({
                ...col,
                minWidth: DOCUMENT_FIT_WEIGHTS[col.field] || col.minWidth || 80,
            }));
        if (hideStatus) {
            cols = cols.filter((col) => !['processed', 'processedBy', 'isLocked'].includes(col.field));
        }
        return cols;
    }, [baseColumns, tableView, hideStatus]);

    const isCheckboxSelectionEnabled = (userTypeContext?.toString() !== Constants.UserType.Client || permissionsState.Premium);
    const {
        containerRef: fitContainerRef,
        columnsWithWidths,
        fitEnabled,
        requestFit,
        handleColumnWidthChange,
    } = useFitTableColumns({
        storageKey: 'documentDataGridV2',
        scope: tableView,
        weightMode: 'column',
        columns: visibleColumns,
        visibilityModel: columnVisibilityModel,
        widths: columnWidths[tableView] || {},
        hasCheckbox: isCheckboxSelectionEnabled,
        apiRef,
        onPersist: (flat) => {
            setColumnWidths((prev) => {
                const next = { ...prev, [tableView]: flat };
                try {
                    localStorage.setItem(STORAGE_KEY_DOCUMENT_COLUMN_WIDTHS, JSON.stringify(next));
                } catch (_) {}
                return next;
            });
        },
    });

    const handleRowEditStop = (params, event) => {
        event.defaultMuiPrevented = true;
    };

    const handleCellEditCommit = (params) => {
        const { id, field, value } = params;

        setRows((prevRows) =>
            prevRows.map((row) => {
                if (row.id === id) {
                    const updatedRow = { ...row, [field]: value };

                    // Update related fields if necessary
                    if (field === 'accountCodeId') {
                        const accountCard = accountCardOptions.find(
                            (option) => option.cardId === value
                        );
                        updatedRow.accountCodeName = accountCard ? accountCard.name : '';
                        updatedRow.accountCaseId = accountCard ? accountCard.accountCaseId : '';

                        if (type === 'Incomes') {
                            updatedRow.debitAccount = value;
                        } else {
                            updatedRow.creditAccount = value;
                        }
                    }

                    return updatedRow;
                }
                return row;
            })
        );
    };


    const selectionCount = selectedRows.length;

    return (
        <div
            className={`${direction}`}
            style={{
                width: '100%',
                height: 'calc(100vh - 420px)',
                minHeight: 420,
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <Box
                className={`${styles.toolbar} ${direction === 'rtl' ? styles.rtl : styles.ltr}`}
                dir={direction}
                sx={{ flexShrink: 0 }}
            >
                <div className={styles.tableViewGroup}>
                    <FormControl size="small" className={styles.tableViewControl}>
                        <InputLabel id="document-view-label">{t('TableView')}</InputLabel>
                        <Select
                            labelId="document-view-label"
                            value={tableView}
                            label={t('TableView')}
                            dir={direction}
                            MenuProps={TABLE_VIEW_MENU_PROPS}
                            onChange={(e) => {
                                const newView = e.target.value;
                                if (newView !== 'custom') {
                                    try {
                                        localStorage.setItem(STORAGE_KEY_DOCUMENT_COLUMN_VISIBILITY_CUSTOM, JSON.stringify(columnVisibilityModel));
                                    } catch (_) {}
                                    setColumnVisibilityModel({ ...DEFAULT_HIDDEN_COLUMNS });
                                } else {
                                    try {
                                        const raw = localStorage.getItem(STORAGE_KEY_DOCUMENT_COLUMN_VISIBILITY_CUSTOM);
                                        if (raw) {
                                            const parsed = JSON.parse(raw);
                                            if (typeof parsed === 'object' && parsed !== null) setColumnVisibilityModel({ ...DEFAULT_HIDDEN_COLUMNS, ...parsed });
                                        }
                                    } catch (_) {}
                                }
                                setTableView(newView);
                            }}
                            sx={{
                                height: 'var(--xtram-control-height, 40px)',
                                minHeight: 'var(--xtram-control-height, 40px)',
                                '& .MuiSelect-select': {
                                    textAlign: direction === 'rtl' ? 'left' : 'right',
                                    py: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                },
                            }}
                        >
                            <MenuItem dir={direction} value="compact">{t('TableViewCompact')}</MenuItem>
                            <MenuItem dir={direction} value="standard">{t('TableViewStandard')}</MenuItem>
                            <MenuItem dir={direction} value="full">{t('TableViewFull')}</MenuItem>
                            <MenuItem dir={direction} value="custom">{t('TableViewCustom')}</MenuItem>
                        </Select>
                    </FormControl>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<ViewColumnIcon />}
                        onClick={(e) => setColumnsPanelAnchor(e.currentTarget)}
                        className={styles.columnsBtn}
                        sx={{
                            minHeight: 'var(--xtram-control-height, 40px)',
                            height: 'var(--xtram-control-height, 40px)',
                            textTransform: 'none',
                            borderRadius: '10px',
                            borderColor: '#e5e7f6',
                            color: '#304FFF',
                            fontSize: 'var(--xtram-table-view-label, 11px)',
                            fontWeight: 600,
                            background: '#f5f6fd',
                            '&:hover': { borderColor: '#304FFF', background: '#eaedff' },
                        }}
                    >
                        {t('ShowHideColumns')}
                    </Button>
                    <FitColumnsButton active={fitEnabled} onClick={requestFit} />
                </div>
                {client?.managesProject && (
                    <label className={`${styles.byProjects}${byProjectsFilterActive ? ` ${styles.checked}` : ''}`}>
                        <Checkbox
                            checked={!!byProjectsFilterActive}
                            onChange={(event) => onByProjectsFilterToggle?.(event.target.checked)}
                            name="ByProjects"
                            size="small"
                            sx={{
                                padding: '2px',
                                color: '#c5c8e0',
                                '&.Mui-checked': { color: '#304fff' },
                            }}
                        />
                        <span>{t('ByProjects')}</span>
                    </label>
                )}
                {client?.managesProject && byProjectsFilterActive && (
                    <div className={styles.projectPickers} dir={direction}>
                        {loadingSegmentations && <CircularProgress size={22} />}
                        <div className={styles.projectField}>
                            <Autocomplete
                                size="small"
                                loading={loadingSegmentations}
                                options={projectAutocompleteOptions}
                                getOptionLabel={(opt) => opt?.label ?? ''}
                                isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                                value={selectedFromSegmentation}
                                onChange={onFromSegmentationChange}
                                PopperComponent={(popperProps) => (
                                    <StyledProjectsPopper {...popperProps} dir={direction} />
                                )}
                                ListboxProps={{ dir: direction }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder={t('FromProject')}
                                        size="small"
                                        variant="standard"
                                        dir={direction}
                                        InputProps={{
                                            ...params.InputProps,
                                            disableUnderline: true,
                                        }}
                                    />
                                )}
                                clearOnBlur={false}
                                dir={direction}
                            />
                        </div>
                        <div className={styles.projectField}>
                            <Autocomplete
                                size="small"
                                loading={loadingSegmentations}
                                options={projectAutocompleteOptions}
                                getOptionLabel={(opt) => opt?.label ?? ''}
                                isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                                value={selectedToSegmentation}
                                onChange={onToSegmentationChange}
                                PopperComponent={(popperProps) => (
                                    <StyledProjectsPopper {...popperProps} dir={direction} />
                                )}
                                ListboxProps={{ dir: direction }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder={t('ToProject')}
                                        size="small"
                                        variant="standard"
                                        dir={direction}
                                        InputProps={{
                                            ...params.InputProps,
                                            disableUnderline: true,
                                        }}
                                    />
                                )}
                                clearOnBlur={false}
                                dir={direction}
                            />
                        </div>
                    </div>
                )}
                <Popover
                    open={Boolean(columnsPanelAnchor)}
                    anchorEl={columnsPanelAnchor}
                    onClose={() => setColumnsPanelAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: direction === 'rtl' ? 'right' : 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: direction === 'rtl' ? 'right' : 'left' }}
                    PaperProps={{ sx: COLUMN_PANEL_PAPER_SX }}
                >
                    {visibleColumns.map((col, index) => {
                        const isChecked = columnVisibilityModel[col.field] !== false;
                        return (
                            <FormControlLabel
                                key={col.field}
                                dir={direction}
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={isChecked}
                                        onChange={(e) => {
                                            setColumnVisibilityModel((prev) => ({
                                                ...DEFAULT_HIDDEN_COLUMNS,
                                                ...prev,
                                                [col.field]: e.target.checked,
                                            }));
                                            setTableView((prev) => (prev === 'custom' ? prev : 'custom'));
                                        }}
                                        sx={{
                                            color: isChecked ? '#304FFF' : '#8b93a7',
                                            '&.Mui-checked': { color: '#304FFF' },
                                        }}
                                    />
                                }
                                label={col.headerName || col.field}
                                sx={{
                                    display: 'flex',
                                    width: '100%',
                                    mx: 0,
                                    py: 0.65,
                                    minHeight: 'var(--xtram-menu-item-height)',
                                    height: 'auto',
                                    cursor: 'pointer',
                                    transition: 'background-color 0.2s ease, color 0.2s ease',
                                    borderBottom: index < visibleColumns.length - 1 ? '1px solid #f0f0f0' : 'none',
                                    backgroundColor: isChecked ? 'rgba(48, 79, 255, 0.1)' : 'transparent',
                                    '& .MuiFormControlLabel-label': {
                                        fontSize: 'var(--xtram-text-sm)',
                                        fontWeight: isChecked ? 500 : 400,
                                        lineHeight: 1.35,
                                        color: isChecked ? '#304FFF' : '#696665',
                                    },
                                    '&:hover': {
                                        backgroundColor: '#304fff',
                                        '& .MuiFormControlLabel-label': { color: '#ffffff' },
                                    },
                                    '&:hover .MuiCheckbox-root': { color: '#ffffff' },
                                    '&:hover .MuiCheckbox-root.Mui-checked': { color: '#ffffff' },
                                }}
                            />
                        );
                    })}
                    <Box sx={{ px: 1.5, py: 0.75, display: 'flex', gap: 1, borderTop: '1px solid #f0f0f0' }}>
                        <Button
                            size="small"
                            onClick={() => {
                                const allVisible = {};
                                visibleColumns.forEach((c) => { allVisible[c.field] = true; });
                                setColumnVisibilityModel((prev) => ({ ...DEFAULT_HIDDEN_COLUMNS, ...prev, ...allVisible }));
                            }}
                            sx={{ textTransform: 'none', fontSize: 'var(--xtram-text-xs, 11px)', color: '#304FFF' }}
                        >
                            {t('ShowAll')}
                        </Button>
                        <Button
                            size="small"
                            onClick={() => {
                                const allHidden = {};
                                visibleColumns.forEach((c) => { allHidden[c.field] = false; });
                                setColumnVisibilityModel((prev) => ({ ...DEFAULT_HIDDEN_COLUMNS, ...prev, ...allHidden }));
                            }}
                            sx={{ textTransform: 'none', fontSize: 'var(--xtram-text-xs, 11px)', color: '#696665' }}
                        >
                            {t('HideAll')}
                        </Button>
                    </Box>
                </Popover>
                {(toolbarExtra || (isCheckboxSelectionEnabled && selectionCount > 0)) && (
                    <Box className={styles.toolbarEnd}>
                        {toolbarExtra}
                        {isCheckboxSelectionEnabled && selectionCount > 0 && (
                            <Box className={styles.selectionBar}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: '1A1F49',
                                            fontWeight: 600,
                                            fontSize: '0.875rem',
                                        }}
                                    >
                                        {t('Selected')}:
                                    </Typography>
                                    <Chip
                                        label={selectionCount}
                                        size="small"
                                        sx={{
                                            backgroundColor: '#304FFF',
                                            color: '#ffffff',
                                            fontWeight: 600,
                                            fontSize: '0.875rem',
                                            height: '22px',
                                        }}
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '4px' }}>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: '#434343',
                                            fontSize: '0.875rem',
                                            fontWeight: 600,
                                        }}
                                    >
                                        {t('NetAmount')}:
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: '#000000',
                                            fontWeight: 600,
                                            fontSize: '0.875rem',
                                            fontFamily: 'monospace',
                                        }}
                                    >
                                        {beautifyNumber(selectedTotals.netAmount)}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '4px' }}>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: '#434343',
                                            fontSize: '0.875rem',
                                            fontWeight: 600,
                                        }}
                                    >
                                        {t('VATAmount')}:
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: '#000000',
                                            fontWeight: 600,
                                            fontSize: '0.875rem',
                                            fontFamily: 'monospace',
                                        }}
                                    >
                                        {beautifyNumber(selectedTotals.vatAmount)}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '4px' }}>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: '1A1F49',
                                            fontSize: '0.875rem',
                                            fontWeight: 600,
                                        }}
                                    >
                                        {t('Amount')}:
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: '#000000',
                                            fontWeight: 600,
                                            fontSize: '0.875rem',
                                            fontFamily: 'monospace',
                                        }}
                                    >
                                        {beautifyNumber(selectedTotals.amount)}
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                    </Box>
                )}
            </Box>
            <LocalizationProvider
                dateAdapter={AdapterDateFns}
                adapterLocale={getLocale()}
            >
                <Box ref={fitContainerRef} sx={{ flex: 1, minHeight: 0, width: '100%' }}>
                    <StyledDataGrid
                        key={`${tableView}-${isSmallScreen ? 'sm' : 'lg'}`}
                        className={fitEnabled ? 'fit-to-screen' : undefined}
                        apiRef={apiRef}
                        rows={rows}
                        columns={columnsWithWidths}
                        onColumnWidthChange={handleColumnWidthChange}
                        checkboxSelection={isCheckboxSelectionEnabled}
                        disableRowSelectionOnClick
                        columnHeaderHeight={42}
                        getRowHeight={() => (fitEnabled ? 48 : 'auto')}
                        getEstimatedRowHeight={() => (fitEnabled ? 48 : 72)}
                        rowSelectionModel={selectedRows}
                        onRowSelectionModelChange={(ids) => handleSelectionModelChange(ids)}
                        getRowId={(row) => row.id}
                        components={{ Toolbar: GridToolbar }}
                        componentsProps={{
                            toolbar: {
                                showQuickFilter: true,
                            },
                        }}
                        pagination
                        sortingOrder={['asc', 'desc']}
                        isRowSelectable={isRowSelectable}
                        onCellDoubleClick={handleCellDoubleClick}
                        processRowUpdate={processRowUpdate}
                        experimentalFeatures={{ newEditingApi: true }}
                        editMode="cell"
                        rowModesModel={rowModesModel}
                        onRowModesModelChange={handleRowModesModelChange}
                        columnVisibilityModel={columnVisibilityModel}
                        onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
                        onCellEditCommit={handleCellEditCommit}
                        onRowEditStop={handleRowEditStop}
                        getRowClassName={getRowClassName}
                        sx={{
                            height: '100%',
                            '& .row--invoiced, & .row--invoiced:hover': {
                                backgroundColor: '#fff6c2',
                            },
                        }}
                        onFilterModelChange={() => {
                        requestAnimationFrame(emitFilteredRows);
                    }}
                        onSortModelChange={() => {
                        requestAnimationFrame(emitFilteredRows);
                    }}
                />
                </Box>
            </LocalizationProvider>

        </div>
    );
});

export default DocumentDataGrid;
